import {FieldType, useCreateFieldMutation} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {
  FieldValuesInput,
  type FieldValueEntry,
} from 'src/FieldsView/FieldValuesInput';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type CreateFieldModalUserProps = {
  onSuccess: () => void;
};

export const CreateFieldModal = ({
  onRequestClose,
  onSuccess,
}: CreateFieldModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState('');
  const [fieldType, setFieldType] = React.useState<FieldType>(FieldType.Enum);
  const [description, setDescription] = React.useState('');
  const [values, setValues] = React.useState<FieldValueEntry[]>([]);
  const [required, setRequired] = React.useState(false);
  const [allowMultiple, setAllowMultiple] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isText = fieldType === FieldType.Text;

  const [createField, {loading}] = useCreateFieldMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await createField({
        variables: {
          name: name.trim(),
          type: fieldType,
          description: description.trim() || null,
          values: isText
            ? []
            : values.map((v) => ({
                value: v.value,
                description: v.description || null,
              })),
          required: isText ? false : required,
          allowMultiple: isText ? false : allowMultiple,
        },
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={
          loading ||
          !name.trim() ||
          !description.trim() ||
          (!isText && values.length === 0)
        }
      >
        Create
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create Field"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g., fulfillment, recurring"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFieldType(FieldType.Enum)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                fieldType === FieldType.Enum
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              Enum
            </button>
            <button
              type="button"
              onClick={() => setFieldType(FieldType.Text)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                fieldType === FieldType.Text
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              Text
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={description}
            onChange={setDescription}
            placeholder="Describe what this field tracks and how it should be used by the LLM (1-3 sentences)"
            rows={3}
          />
        </div>

        {!isText && (
          <>
            <FieldValuesInput
              values={values}
              onChange={setValues}
              onError={setError}
            />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="required" className="text-sm">
                  Required on all transactions
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allowMultiple"
                  checked={allowMultiple}
                  onChange={(e) => setAllowMultiple(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="allowMultiple" className="text-sm">
                  Allow multiple values
                </label>
              </div>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};
